import { useEffect, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useFetchTrigger } from '../../lib/useFetchTrigger';
import type { Booking } from './types';

// Every booking currently 'arrived' — the live in-residence set, date-UNBOUNDED.
// An overstay (checkout passed but never marked collected) is still physically in
// the kennel and still 'arrived', so it must show — exactly like the Bookings
// 'arrived' chip, which is also date-agnostic. The Today page's ±2-day window
// (statWindow) is for the DESK — today's drop-offs/collections — NOT residence;
// sourcing the card from that window dropped arrived dogs outside it (54/55).
//
// This fetch feeds the In-residence card's dog-avatar LIST only. The headline
// COUNT is NOT computed from it — it reads the uncapped /bookings/stats arrived
// aggregate (see TodayView), so the number has no limit. The limit here bounds
// only how many avatars we pull for the preview (the card shows "+N more"); it is
// the endpoint max and far above kennel capacity, so the list is whole in
// practice. No from/to: the set is the status, not a date range.
export const ARRIVED_RESIDENTS_QUERY = '/bookings?status=arrived&limit=200';

export interface UseArrivedResidents {
  bookings: Booking[];
  // True only while enabled and the first fetch has not yet settled — lets the
  // Today page hold its skeleton instead of flashing "No dogs in" then filling.
  loading: boolean;
  reload: () => void;
}

// Mirrors useBookingStats: a silent stale-while-revalidate fetch keyed on the
// activation tick (on-return) and mutation tick (a same-page status write), so
// marking a dog arrived/collected updates the card without a flash. Disabled
// (no fetch) on any day but the live today — other days read the windowed set's
// date-based projection, which needs no extra request.
export function useArrivedResidents(enabled: boolean): UseArrivedResidents {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loaded, setLoaded] = useState(false);
  // The shared SWR trigger core (lib/useFetchTrigger): reload() and the
  // activation+mutation tick both fire a cache-bypassing refetch; the set on
  // screen holds across it.
  const trigger = useFetchTrigger();

  useEffect(() => {
    if (!enabled) return;
    const run = trigger.begin();
    api<Booking[]>(ARRIVED_RESIDENTS_QUERY, run.init)
      .then((body) => {
        if (!run.isCurrent()) return;
        run.markContent();
        setLoaded(true);
        if (body.data) setBookings(body.data);
      })
      .catch((e: unknown) => {
        if (!run.isCurrent()) return;
        setLoaded(true); // a failed first load still releases the skeleton
        if (e instanceof ApiError && e.status === 401) return; // already redirected
        // A failed silent revalidation holds the last-known set (no flash).
      });
    return run.cancel;
  }, [enabled, trigger.nonce, trigger.tick]);

  return { bookings, loading: enabled && !loaded, reload: trigger.reload };
}
